import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { createCustomerProductList } from "./schema/accounts/create-customer-product-list";
import { getCustomerProductList } from "./schema/accounts/get-customer-product-list";
import { addMerchantOverrides } from "./schema/add-merchant-overrides";
import { addReturnItem } from "./schema/add-return-item";
import { changeResetPoint } from "./schema/change-reset-point";
import { convertTokenToCustomerAuth } from "./schema/convert-token-to-customer-auth";
import {
  addProductToOrderlessDraftReturn,
  createDraftReturn,
  createOrderlessDraftReturn,
} from "./schema/create-draft-return";
import { createDraftReturnFromSource } from "./schema/create-draft-return-from-source";
import { getCurrentFlow } from "./schema/get-current-flow";
import { getExchangeRecommendations } from "./schema/get-exchange-recommendations";
import { getFulfillmentOrdersByOrder } from "./schema/get-fulfillment-orders-by-order";
import { getOrderRegistrationEligibility } from "./schema/get-order-registration-eligibility";
import { getOrdersFromSearch } from "./schema/get-orders-from-search";
import { getProductFilterById } from "./schema/get-product-filter-by-id";
import { getReturn } from "./schema/get-return";
import { getReturnCreationStatus } from "./schema/get-return-creation-status";
import { getReturnsByOrderId } from "./schema/get-returns-by-order-id";
import { getSharedList } from "./schema/get-shared-list";
import { getSubmissionWorkflowStatus } from "./schema/get-submission-workflow-status";
import { getSuggestedVariantOptions } from "./schema/get-suggested-variant-options";
import { giftAuth } from "./schema/gift-auth";
import { giftReceiptAuth } from "./schema/gift-receipt-auth";
import { createGiftReceiptRequest } from "./schema/gift-receipt-requests/create-gift-receipt-request";
import { createGiftReceipt } from "./schema/gift-receipts/create-gift-receipt";
import { deleteGiftReceipt } from "./schema/gift-receipts/delete-gift-receipt";
import { getGiftReceipt } from "./schema/gift-receipts/get-gift-receipt";
import { getGiftReceiptCount } from "./schema/gift-receipts/get-gift-receipt-count";
import { getGiftReceipts } from "./schema/gift-receipts/get-gift-receipts";
import { sendGiftReceipt } from "./schema/gift-receipts/send-gift-receipt";
import { hasMultipleOrders } from "./schema/has-multiple-orders";
import {
  getSignupForm,
  handleMarketingEmailUnsubscribe,
  logSignupFormAnalytics,
  submitSignupFormStep,
} from "./schema/marketing";
import { getCustomFieldOptions } from "./schema/marketing/get-custom-field-options";
import {
  addNewItems,
  calculateReturnTotals,
  changeAddress,
  editPendingItemQuantity,
  editReturnableItem,
  getDraftReturn,
  getFaq,
  getLocations,
  getMarketingTriggered,
  getOrderId,
  getOrders,
  getRecommendedProductsByProvider,
  getRegistrationDiscounts,
  getRegistrations,
  getSatisfactionResponse,
  getShippingEstimate,
  getStripePublicKey,
  getWarrantyEligibleProducts,
  getWarrantyRegistration,
  getWarrantyRegistrationFlow,
  isShopAppDisabled,
  rejectReturnItems,
  removeNewItems,
  removeReturnItem,
  saveSatisfactionResponse,
  setCompensationMethod,
  setProvisionType,
  setShipmentMethodInfo,
  submitReturn,
  submitWarrantyRegistration,
  toggleSelectReturnItem,
  updatePickupVariant,
  verifyAddress,
} from "./schema/schemas";
import { sendEmailAuthLink } from "./schema/send-email-auth-link";
import { shopAppTempLogin } from "./schema/shop-app-temp-login";
import { singleOrderAuth } from "./schema/single-order-auth";
import { skipAddReturnItems } from "./schema/skip-add-return-items";
import { convertSocialAuthToCustomerAuth } from "./schema/social-auth";
import { submitDraftReturn } from "./schema/submit-draft-return";
import { getTnplContractForOrder } from "./schema/tnpl/get-order-tnpl-contract";

// Split up the RPC schemas to prevent the RPC definition type from getting too large for TypeScript to emit

const changeAddressRpcSchema = { changeAddress };
const editReturnableItemRpcSchema = { editReturnableItem };
const createDraftReturnRpcSchema = {
  createDraftReturn,
  createOrderlessDraftReturn,
  addProductToOrderlessDraftReturn,
  createDraftReturnFromSource,
};
const shopAppTempLoginSchema = { shopAppTempLogin };
const editPendingItemQuantityRpcSchema = { editPendingItemQuantity };
const getDraftReturnRpcSchema = { getDraftReturn };
const getShippingEstimateRpcSchema = { getShippingEstimate };
const getRecommendedProductsByProviderRpcSchema = {
  getRecommendedProductsByProvider,
};
const getStripePublicKeyRpcSchema = { getStripePublicKey };
const setShipmentMethodInfoRpcSchema = { setShipmentMethodInfo };
const setCompensationMethodRpcSchema = { setCompensationMethod };
const addReturnItemRpcSchema = { addReturnItem };
const skipAddReturnItemsRpcSchema = { skipAddReturnItems };
const removeReturnItemRpcSchema = { removeReturnItem };
const submitReturnRpcSchema = { submitReturn };
const submitDraftReturnRpcSchema = { submitDraftReturn };
const rejectReturnItemsRpcSchema = { rejectReturnItems };
const addNewItemsRpcSchema = { addNewItems };
const removeNewItemsRpcSchema = { removeNewItems };
const verifyAddressRpcSchema = { verifyAddress };
const calculateReturnTotalsRpcSchema = { calculateReturnTotals };
const getLocationsRpcSchema = { getLocations };
const getSatisfactionResponseRpcSchema = { getSatisfactionResponse };
const saveSatisfactionResponseSchema = { saveSatisfactionResponse };
const getFaqSchema = { getFaq };
const getSharedListRpcSchema = { getSharedList };
const customerProductListSchema = {
  getCustomerProductList,
  createCustomerProductList,
};
const getSubmissionWorkflowStatusSchema = { getSubmissionWorkflowStatus };
const getWarrantyEligibleProductsSchema = { getWarrantyEligibleProducts };
const getWarrantyRegistrationFlowSchema = { getWarrantyRegistrationFlow };
const getWarrantyRegistrationSchema = { getWarrantyRegistration };
const getRegistrationDiscountsSchema = { getRegistrationDiscounts };
const getMarketingTriggeredSchema = { getMarketingTriggered };
const submitWarrantyRegistrationSchema = { submitWarrantyRegistration };
const fulfillmentOrderSchema = { getFulfillmentOrdersByOrder };
const getOrderIdSchema = { getOrderId };
const toggleSelectReturnItemSchema = { toggleSelectReturnItem };
const setProvisionTypeRpcSchema = { setProvisionType };
const getReturnRpcSchema = { getReturn };
const marketingSchema = {
  getSignupForm,
  submitSignupFormStep,
  logSignupFormAnalytics,
  handleMarketingEmailUnsubscribe,
  getCustomFieldOptions,
};
const sendEmailAuthLinkSchema = { sendEmailAuthLink };
const singleOrderAuthSchema = { singleOrderAuth };
const convertTokenToCustomerAuthSchema = { convertTokenToCustomerAuth };
const convertSocialAuthToCustomerAuthSchema = {
  convertSocialAuthToCustomerAuth,
};
const getOrdersRpcSchema = { getOrders };
const getOrdersFromSearchRpcSchema = { getOrdersFromSearch };
const isShopAppDisabledRpcSchema = { isShopAppDisabled };
const updatePickupVariantSchema = { updatePickupVariant };
const getProductFilterByIdSchema = { getProductFilterById };
const getReturnCreationStatusSchema = { getReturnCreationStatus };
const getRegistrationsSchema = { getRegistrations };
const getOrderRegistrationEligibilitySchema = {
  getOrderRegistrationEligibility,
};

const getTnplContractForOrderSchema = { getTnplContractForOrder };
const giftAuthSchema = { giftAuth };
const giftReceiptAuthSchema = { giftReceiptAuth };
const getSuggestedVariantOptionsSchema = { getSuggestedVariantOptions };
const changeResetPointSchema = { changeResetPoint };
const getCurrentFlowSchema = { getCurrentFlow };
const getExchangeRecommendationsSchema = { getExchangeRecommendations };
const hasMultipleOrdersSchema = { hasMultipleOrders };
const addMerchantOverridesSchema = { addMerchantOverrides };
const getReturnsByOrderIdSchema = { getReturnsByOrderId };

const giftReceipts = {
  getGiftReceiptCount,
  createGiftReceipt,
  getGiftReceipt,
  getGiftReceipts,
  deleteGiftReceipt,
  sendGiftReceipt,
  createGiftReceiptRequest,
};

export const rpcDefinition = {
  ...changeAddressRpcSchema,
  ...createDraftReturnRpcSchema,
  ...editReturnableItemRpcSchema,
  ...editPendingItemQuantityRpcSchema,
  ...getDraftReturnRpcSchema,
  ...getShippingEstimateRpcSchema,
  ...getRecommendedProductsByProviderRpcSchema,
  ...getStripePublicKeyRpcSchema,
  ...setShipmentMethodInfoRpcSchema,
  ...setCompensationMethodRpcSchema,
  ...addReturnItemRpcSchema,
  ...skipAddReturnItemsRpcSchema,
  ...removeReturnItemRpcSchema,
  ...submitReturnRpcSchema,
  ...submitDraftReturnRpcSchema,
  ...rejectReturnItemsRpcSchema,
  ...addNewItemsRpcSchema,
  ...removeNewItemsRpcSchema,
  ...verifyAddressRpcSchema,
  ...calculateReturnTotalsRpcSchema,
  ...getLocationsRpcSchema,
  ...getSatisfactionResponseRpcSchema,
  ...saveSatisfactionResponseSchema,
  ...getFaqSchema,
  ...getSharedListRpcSchema,
  ...customerProductListSchema,
  ...getWarrantyEligibleProductsSchema,
  ...getWarrantyRegistrationFlowSchema,
  ...getWarrantyRegistrationSchema,
  ...submitWarrantyRegistrationSchema,
  ...getRegistrationDiscountsSchema,
  ...fulfillmentOrderSchema,
  ...getOrderIdSchema,
  ...toggleSelectReturnItemSchema,
  ...setProvisionTypeRpcSchema,
  ...getReturnRpcSchema,
  ...marketingSchema,
  ...getOrdersRpcSchema,
  ...isShopAppDisabledRpcSchema,
  ...updatePickupVariantSchema,
  ...getSubmissionWorkflowStatusSchema,
  ...getProductFilterByIdSchema,
  ...getReturnCreationStatusSchema,
  ...getMarketingTriggeredSchema,
  ...getRegistrationsSchema,
  ...getOrderRegistrationEligibilitySchema,
  ...getTnplContractForOrderSchema,
  ...sendEmailAuthLinkSchema,
  ...convertTokenToCustomerAuthSchema,
  ...convertSocialAuthToCustomerAuthSchema,
  ...shopAppTempLoginSchema,
  ...giftAuthSchema,
  ...giftReceiptAuthSchema,
  ...singleOrderAuthSchema,
  ...getSuggestedVariantOptionsSchema,
  ...changeResetPointSchema,
  ...getCurrentFlowSchema,
  ...getExchangeRecommendationsSchema,
  ...hasMultipleOrdersSchema,
  ...addMerchantOverridesSchema,
  ...getOrdersFromSearchRpcSchema,
  ...getReturnsByOrderIdSchema,
  ...giftReceipts,
};

export type CustomerPortalRpcDefinition = InferRpcDefinition<
  typeof rpcDefinition
>;
